import { useState } from 'react'
import { Alert, App, Button, Card, Flex, Select, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { CenterFormModal } from '@/features/network/CenterFormModal'
import { NetworkDeleteModal, type NetworkDeleteTarget } from '@/features/network/NetworkDeleteModal'
import { VispFormModal } from '@/features/network/VispFormModal'
import { VispQuotaModal } from '@/features/network/VispQuotaModal'
import { VispAccountsDrawer } from '@/features/network/VispAccountsDrawer'
import { RoutersModal, type RoutersTarget } from '@/features/network/RoutersModal'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import {
  assignVispSubscribersBranch,
  fetchCenters,
  fetchRouters,
  fetchVisps,
  routerLabel,
  setCenterRouters,
  setVispRouters,
  type NetworkCenter,
  type NetworkRouter,
  type NetworkStatus,
  type Visp,
} from '@/features/network/networkApi'

export function NetworkVispsPage() {
  const { t } = useTranslation()
  const { modal, message } = App.useApp()
  const queryClient = useQueryClient()
  const { options: branchChoices } = useActiveBranches()
  const branchNames = new Map(branchChoices.map((b) => [b.id, b.name]))
  const [centerFilter, setCenterFilter] = useState<string | null>(null)
  const [centerForm, setCenterForm] = useState<{ open: boolean; center: NetworkCenter | null }>({
    open: false,
    center: null,
  })
  const [vispForm, setVispForm] = useState<{ open: boolean; visp: Visp | null }>({ open: false, visp: null })
  const [routersFor, setRoutersFor] = useState<RoutersTarget | null>(null)
  const [deleting, setDeleting] = useState<NetworkDeleteTarget | null>(null)
  const [quotaFor, setQuotaFor] = useState<Visp | null>(null)
  const [accountsFor, setAccountsFor] = useState<Visp | null>(null)

  const centers = useQuery({ queryKey: queryKeys.network.centers, queryFn: fetchCenters })
  const visps = useQuery({ queryKey: queryKeys.network.visps, queryFn: fetchVisps })
  const routers = useQuery({ queryKey: queryKeys.network.routers, queryFn: fetchRouters })

  const allVisps = visps.data ?? []
  const shownVisps = centerFilter ? allVisps.filter((v) => v.centerId === centerFilter) : allVisps
  const coveringAll = allVisps.filter((v) => v.coversAllNas)
  const routerNames = new Map((routers.data ?? []).map((r) => [r.nasId, routerLabel(r)]))

  const centerRouters = (center: NetworkCenter): RoutersTarget => ({
    title: t('network.centers.routersTitle', { name: center.name }),
    hint: t('network.centers.routersHint'),
    nasIds: center.nasIds,
    allowedNasIds: null,
    save: (nasIds) => setCenterRouters(center.id, nasIds),
  })

  const vispRouters = (visp: Visp): RoutersTarget => {
    const center = centers.data?.find((c) => c.id === visp.centerId)
    return {
      title: t('network.visps.routersTitle', { name: visp.name }),
      hint: center && !center.coversAllNas ? t('network.visps.routersCenterHint') : t('network.visps.routersHint'),
      nasIds: visp.nasIds,
      allowedNasIds: center && !center.coversAllNas ? center.nasIds : null,
      save: (nasIds) => setVispRouters(visp.id, nasIds),
    }
  }

  const branchTags = (ids: readonly string[]) =>
    ids.length === 0 ? (
      <Typography.Text type="secondary">{t('branch.none')}</Typography.Text>
    ) : (
      <Flex wrap gap={4}>
        {ids.map((id) => (
          <Tag key={id}>{branchNames.get(id) ?? '…'}</Tag>
        ))}
      </Flex>
    )

  const assignSubscribers = (visp: Visp) =>
    modal.confirm({
      title: t('branch.assignByVispTitle', { name: visp.name }),
      content: t('branch.assignByVispConfirm', { branch: branchNames.get(visp.branchIds[0] ?? '') ?? '' }),
      okText: t('branch.assignByVisp'),
      cancelText: t('common.cancel'),
      onOk: () =>
        assignVispSubscribersBranch(visp.id)
          .then((moved) => {
            void message.success(t('branch.assignByVispDone', { count: moved }))
            void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
          })
          .catch((err: unknown) => {
            void message.error(translateError(err, t))
          }),
    })

  const routerTags = (nasIds: readonly number[]) =>
    nasIds.length === 0 ? (
      <Typography.Text type="secondary">{t('network.visps.noRouters')}</Typography.Text>
    ) : (
      <Flex wrap gap={4}>
        {nasIds.map((id) => (
          <Tag key={id}>{routerNames.get(id) ?? `#${id}`}</Tag>
        ))}
      </Flex>
    )

  const centerColumns: ColumnsType<NetworkCenter> = [
    {
      key: 'name',
      title: t('network.name'),
      width: 220,
      render: (_, row) => (
        <Space size={6}>
          <Typography.Text strong>{row.name}</Typography.Text>
          {row.isDefault ? <Tag>{t('network.default')}</Tag> : null}
        </Space>
      ),
    },
    { key: 'description', title: t('network.description'), render: (_, row) => row.description ?? '—' },
    { key: 'status', title: t('network.status.label'), width: 130, render: (_, row) => <StatusTag status={row.status} /> },
    {
      key: 'routers',
      title: t('network.visps.routers'),
      render: (_, row) =>
        row.coversAllNas ? <Tag color="blue">{t('network.visps.allRouters')}</Tag> : routerTags(row.nasIds),
    },
    { key: 'branches', title: t('branch.labelPlural'), render: (_, row) => branchTags(row.branchIds) },
    { key: 'vispCount', title: t('network.centers.vispCount'), width: 110, align: 'right', dataIndex: 'vispCount' },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            { key: 'visps', label: t('network.centers.showVisps'), onClick: () => setCenterFilter(row.id) },
            {
              key: 'edit',
              label: t('common.edit'),
              permission: 'network.visp.manage',
              onClick: () => setCenterForm({ open: true, center: row }),
            },
            {
              key: 'routers',
              label: t('network.visps.assignRouters'),
              permission: 'network.visp.manage',
              hidden: row.isDefault,
              onClick: () => setRoutersFor(centerRouters(row)),
            },
            {
              key: 'delete',
              label: t('network.delete'),
              permission: 'network.visp.manage',
              danger: true,
              divider: true,
              hidden: row.isDefault,
              onClick: () => setDeleting({ kind: 'center', id: row.id, name: row.name }),
            },
          ]}
        />
      ),
    },
  ]

  const vispColumns: ColumnsType<Visp> = [
    {
      key: 'name',
      title: t('network.name'),
      width: 220,
      render: (_, row) => (
        <Space size={6}>
          <Typography.Text strong>{row.name}</Typography.Text>
          {row.isDefault ? <Tag>{t('network.default')}</Tag> : null}
        </Space>
      ),
    },
    { key: 'center', title: t('network.visps.center'), render: (_, row) => row.centerName },
    { key: 'branches', title: t('branch.labelPlural'), render: (_, row) => branchTags(row.branchIds) },
    { key: 'status', title: t('network.status.label'), width: 130, render: (_, row) => <StatusTag status={row.status} /> },
    {
      key: 'routers',
      title: t('network.visps.routers'),
      render: (_, row) =>
        row.coversAllNas ? <Tag color="blue">{t('network.visps.allRouters')}</Tag> : routerTags(row.nasIds),
    },
    {
      key: 'quota',
      title: t('network.visps.quota'),
      width: 110,
      align: 'right',
      render: (_, row) =>
        row.quota == null ? (
          <Typography.Text type="secondary">{t('network.visps.unlimited')}</Typography.Text>
        ) : (
          row.quota
        ),
    },
    {
      key: 'accounts',
      title: t('network.visps.accounts'),
      render: (_, row) => <AccountCounters visp={row} />,
    },
    {
      key: 'subscriberCount',
      title: t('network.visps.subscriberCount'),
      width: 120,
      align: 'right',
      dataIndex: 'subscriberCount',
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('common.edit'),
              permission: 'network.visp.manage',
              onClick: () => setVispForm({ open: true, visp: row }),
            },
            {
              key: 'routers',
              label: t('network.visps.assignRouters'),
              permission: 'network.visp.manage',
              hidden: row.coversAllNas,
              onClick: () => setRoutersFor(vispRouters(row)),
            },
            {
              key: 'assignBranch',
              label: t('branch.assignByVisp'),
              permission: 'network.visp.manage',
              hidden: row.branchIds.length !== 1,
              onClick: () => {
                assignSubscribers(row)
              },
            },
            {
              key: 'accounts',
              label: t('network.visps.availableAccounts'),
              permission: 'subscriber.create',
              hidden: row.quota == null,
              onClick: () => setAccountsFor(row),
            },
            {
              key: 'quota',
              label: t('network.visps.changeQuota'),
              permission: 'network.visp.quota',
              hidden: row.isDefault || !row.loginPrefix,
              onClick: () => setQuotaFor(row),
            },
            {
              key: 'delete',
              label: t('network.delete'),
              permission: 'network.visp.manage',
              danger: true,
              divider: true,
              hidden: row.isDefault,
              onClick: () => setDeleting({ kind: 'visp', id: row.id, name: row.name }),
            },
          ]}
        />
      ),
    },
  ]

  const routerColumns: ColumnsType<NetworkRouter> = [
    { key: 'router', title: t('network.routers.router'), render: (_, row) => routerLabel(row) },
    {
      key: 'centers',
      title: t('network.routers.centers'),
      render: (_, row) =>
        row.centers.length === 0 ? (
          <Typography.Text type="secondary">{t('network.routers.noCenter')}</Typography.Text>
        ) : (
          <Flex wrap gap={4}>
            {row.centers.map((c) => (
              <Tag key={c.id}>{c.name}</Tag>
            ))}
          </Flex>
        ),
    },
    {
      key: 'visps',
      title: t('network.routers.visps'),
      render: (_, row) =>
        row.visps.length === 0 ? (
          <Typography.Text type="secondary">{t('network.routers.noVisp')}</Typography.Text>
        ) : (
          <Flex wrap gap={4}>
            {row.visps.map((v) => (
              <Tag key={v.id}>{v.name}</Tag>
            ))}
          </Flex>
        ),
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.networkVisps')} description={t('pageIntro.networkVisps')} />
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          title={t('network.centers.title')}
          extra={
            <Can permission="network.visp.manage">
              <Button icon={<PlusOutlined />} onClick={() => setCenterForm({ open: true, center: null })}>
                {t('network.centers.create')}
              </Button>
            </Can>
          }
        >
          {centers.isError ? (
            <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(centers.error, t)} />
          ) : null}
          <Table<NetworkCenter>
            rowKey="id"
            size="small"
            columns={centerColumns}
            dataSource={centers.data ?? []}
            loading={centers.isPending}
            pagination={false}
            scroll={{ x: 800 }}
          />
        </Card>

        <Card
          title={t('network.visps.title')}
          extra={
            <Space wrap>
              <Select
                allowClear
                style={{ minWidth: 200 }}
                placeholder={t('network.visps.allCenters')}
                value={centerFilter ?? undefined}
                onChange={(value?: string) => setCenterFilter(value ?? null)}
                options={(centers.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
              <Can permission="network.visp.manage">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setVispForm({ open: true, visp: null })}
                  disabled={!centers.isSuccess}
                >
                  {t('network.visps.create')}
                </Button>
              </Can>
            </Space>
          }
        >
          {visps.isError ? (
            <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(visps.error, t)} />
          ) : null}
          <Table<Visp>
            rowKey="id"
            size="small"
            columns={vispColumns}
            dataSource={shownVisps}
            loading={visps.isPending}
            pagination={false}
            scroll={{ x: 1200 }}
          />
        </Card>

        <Card title={t('network.routers.title')}>
          {routers.isError ? (
            <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(routers.error, t)} />
          ) : null}
          {coveringAll.length > 0 ? (
            <Typography.Paragraph type="secondary">
              {t('network.routers.coversAllNote', { names: coveringAll.map((v) => v.name).join(', ') })}
            </Typography.Paragraph>
          ) : null}
          <Table<NetworkRouter>
            rowKey="nasId"
            size="small"
            columns={routerColumns}
            dataSource={routers.data ?? []}
            loading={routers.isPending}
            pagination={false}
            scroll={{ x: 640 }}
          />
        </Card>
      </Space>

      <CenterFormModal
        open={centerForm.open}
        center={centerForm.center}
        onClose={() => setCenterForm((current) => ({ ...current, open: false }))}
      />
      <VispFormModal
        open={vispForm.open}
        visp={vispForm.visp}
        centers={centers.data ?? []}
        defaultCenterId={centerFilter}
        onClose={() => setVispForm((current) => ({ ...current, open: false }))}
      />
      <RoutersModal target={routersFor} onClose={() => setRoutersFor(null)} />
      <NetworkDeleteModal target={deleting} onClose={() => setDeleting(null)} />
      <VispQuotaModal visp={quotaFor} onClose={() => setQuotaFor(null)} />
      <VispAccountsDrawer visp={accountsFor} onClose={() => setAccountsFor(null)} />
    </>
  )
}

const COUNTERS = ['allocated', 'used', 'available', 'active', 'suspended', 'expired'] as const

function AccountCounters({ visp }: { visp: Visp }) {
  const { t } = useTranslation()
  return (
    <Flex wrap gap={4}>
      {COUNTERS.map((key) => (
        <Tag key={key} color={key === 'available' && visp.quota != null && visp.accounts.available === 0 ? 'red' : undefined}>
          {t(`network.visps.counters.${key}`)}: {visp.accounts[key]}
        </Tag>
      ))}
    </Flex>
  )
}

function StatusTag({ status }: { status: NetworkStatus }) {
  const { t } = useTranslation()
  return status === 'ACTIVE' ? (
    <Tag color="green">{t('network.status.ACTIVE')}</Tag>
  ) : (
    <Tag>{t('network.status.DISABLED')}</Tag>
  )
}
